import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

import { DocumentPreviewComponent, getPreviewExtension } from '../components/document-preview/document-preview.component';

const MODAL_OPTIONS = { fullscreen: true, windowClass: 'isophi-document-preview-modal' };

/**
 * Opens the full screen in-app document preview (Download / Print / Close).
 */
@Injectable({
  providedIn: 'root',
})
export class DocumentPreviewService {
  private readonly modalService = inject(NgbModal);

  /** Preview of already loaded content, e.g. a generated PDF print template. */
  openBlob(blob: Blob, fileName: string, mimeType = 'application/pdf'): NgbModalRef {
    const modalRef = this.modalService.open(DocumentPreviewComponent, MODAL_OPTIONS);
    const component = modalRef.componentInstance as DocumentPreviewComponent;
    component.blob = blob.type === mimeType ? blob : new Blob([blob], { type: mimeType });
    component.mimeType = mimeType;
    component.fileName = fileName;
    return modalRef;
  }

  /** Preview of a remote file (e.g. an uploaded document). `title` is the displayed file name. */
  openUrl(url: string, title?: string | null): NgbModalRef {
    const modalRef = this.modalService.open(DocumentPreviewComponent, MODAL_OPTIONS);
    const component = modalRef.componentInstance as DocumentPreviewComponent;
    component.url = url;
    component.fileName = this.getFileName(url, title);
    return modalRef;
  }

  private getFileName(url: string, title?: string | null): string {
    const urlFileName = decodeURIComponent(url.split(/[?#]/)[0].split('/').pop() ?? '');
    const name = title?.trim() || urlFileName || 'document';
    const urlExtension = getPreviewExtension(urlFileName);
    // keep the real extension so that the type is recognised and the downloaded file opens correctly
    if (urlExtension && getPreviewExtension(name) !== urlExtension) {
      return `${name}.${urlExtension}`;
    }
    return name;
  }
}
